import React from 'react';
import { Badge } from 'react-bootstrap';

const SideNavigation = ({ 
  categories, 
  activeSection, 
  setActiveSection, 
  completionStats,
  contactData,
  errorCounts = {}, // Add errorCounts prop with default empty object
  fileUploadStats = { completed: 0, total: 2 },
  fileUploadsComplete = false,
  hasSecondaryContact = false
}) => {
  // Check if all form fields are completed for validation
  const allFormFieldsCompleted = Object.entries(completionStats)
    .filter(([category]) => category !== 'File Uploads' && category !== 'Submit to Lender')
    .every(([_, stats]) => stats.completed === stats.total && stats.total > 0);

  // Combine both checks for overall completion
  const allFieldsCompleted = allFormFieldsCompleted && fileUploadsComplete;

  // Map long category names to shorter versions
  const getShortenedCategoryName = (category) => {
    switch(category) {
      case 'Contact Information':
        return 'Contact';
      case 'Business Information':
        return 'Business';
      case 'File Uploads':
        return 'Files';
      case 'Submit to Lender':
        return 'Submit';
      default:
        return category;
    }
  };

  // Get status message for Submit section
  const getSubmitStatusMessage = () => {
    // Check if all stats show completion
    const fieldsComplete = Object.entries(completionStats)
      .filter(([category]) => category !== 'File Uploads' && category !== 'Submit to Lender')
      .every(([_, stats]) => stats.completed === stats.total && stats.total > 0);
    
    if (fieldsComplete && fileUploadsComplete) {
      return 'Ready';
    } else if (!fieldsComplete && !fileUploadsComplete) {
      return 'All Required'; 
    } else if (!fileUploadsComplete) {
      return 'Files Required';
    } else if (!fieldsComplete) {
      return 'Fields Required';
    } else {
      // Default to Ready if all checks pass but something else went wrong
      return 'Ready';
    }
  };

  return (
    <div className="side-navigation">
      
      
      {/* Contact Info */}
      {contactData && (
        <div className="contact-info">
          <div className="contact-name">
            {contactData.properties.firstname?.value} {contactData.properties.lastname?.value}
          </div>
          <div className="contact-email">
            {contactData.properties.email?.value}
          </div>
          <div className="contact-id mt-1">
            <small className="text-muted">ID: {contactData.vid}</small>
          </div>
        </div>
      )}
      
      {/* Navigation Items */}
      <div className="nav-items">
        {categories.map((category, index) => {
          const stats = completionStats[category] || { completed: 0, total: 0 };
          const isComplete = stats.completed === stats.total && stats.total > 0;
          const isActive = activeSection === index;
          const errorCount = errorCounts[category] || 0;
          
          // Special handling for File Uploads category
          if (category === 'File Uploads') {
            return (
              <div 
                key={category}
                className={`nav-item ${isActive ? 'active-option2' : ''} ${fileUploadsComplete ? 'completed' : ''}`}
                onClick={() => setActiveSection(index)}
                style={{ position: isActive ? 'relative' : 'static' }}
              >
                <div className="d-flex justify-content-between align-items-center w-100">
                  <span>{getShortenedCategoryName(category)}</span>
                  <span className={`count-text ${fileUploadsComplete ? 'text-success' : ''}`}>
                    {fileUploadStats.completed}/{fileUploadStats.total}
                  </span>
                </div>
              </div>
            );
          }
          
          // Handle Submit to Lender section at the end
          if (index === categories.length - 1 && category === 'Submit to Lender') {
            const statusMessage = getSubmitStatusMessage();
            const statusClass = allFieldsCompleted ? 'text-success ready-text' : 'text-danger';
            
            return (
              <div 
                key={category}
                className={`nav-item ${isActive ? 'active-option2' : ''} ${allFieldsCompleted ? 'completed' : ''}`}
                onClick={() => setActiveSection(index)}
                style={{ position: isActive ? 'relative' : 'static' }}
              >
                <div className="d-flex justify-content-between align-items-center w-100">
                  <span>Submit</span>
                  <span className={`count-text ${statusClass}`}>
                    {statusMessage}
                  </span>
                </div>
              </div>
            );
          }
          
          // Regular item for all other categories
          return (
            <div 
              key={category}
              className={`nav-item ${isActive ? 'active-option2' : ''} ${isComplete ? 'completed' : ''}`}
              onClick={() => setActiveSection(index)}
              style={{ position: isActive ? 'relative' : 'static' }}
            >
              <div className="d-flex justify-content-between align-items-center w-100">
                <span>{getShortenedCategoryName(category)}</span>
                <div className="status-container">
                  {errorCount > 0 && (
                    <div className="error-dot"></div>
                  )}
                  <span className={`count-text ${isComplete ? 'text-success' : (errorCount > 0 ? 'text-danger' : '')}`}>
                    {stats.completed}/{stats.total}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SideNavigation; 